import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { WhenLoaded } from '@openx/components-core/lib/Loader/WhenLoaded.tsx';
import { DEFAULT_CURRENCY } from '@openx/constants/api.ts';
import type { DealFormData } from '@openx/types/deal.ts';
import type { FetchTpfBoostedBidPricePayload } from '@openx/utils/apiCalls/fetchTpfBoostedBidPrice.ts';
import { useDebouncedValue } from '@openx/utils/hooks/useDebouncedValue.ts';
import { priceFormat } from '@openx/utils/lib/priceFormat.ts';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDealContext } from '../../../../utils/context/DealContext.tsx';

const MINIMUM_DEAL_PRICE = '0.01';

export const ThirdPartyFeeInfo = () => {
  const { useTpfBoostedBidPriceFetch } = useDealContext();
  const { formState } = useFormContext<DealFormData>();
  const { t } = useTranslation();
  const observableCurrency = useWatch<DealFormData, 'currency'>({ name: 'currency' });
  const observableDealPrice = useWatch<DealFormData, 'deal_price'>({ name: 'deal_price' });
  const observableThirdPartyFeesConfig = useWatch<DealFormData, 'third_party_fees_config'>({
    name: 'third_party_fees_config',
  });

  const observableThirdPartyFeesConfigEnabled = useWatch<DealFormData, 'third_party_fees_config_enabled'>({
    name: 'third_party_fees_config_enabled',
  });

  const [isDisabled, payload] = useDebouncedValue([
    !!formState.errors.deal_price ||
      !!formState.errors.third_party_fees_config ||
      !observableThirdPartyFeesConfigEnabled,
    {
      dealPriceCpm: observableDealPrice || MINIMUM_DEAL_PRICE,
      thirdPartyFeesConfig: observableThirdPartyFeesConfig.map(config => ({
        gross_cpm_cap: config.gross_cpm_cap,
        gross_share: config.gross_share,
        revenue_method: config.revenue_method,
      })),
    } satisfies FetchTpfBoostedBidPricePayload,
  ] as const);

  const { data, isLoading } = useTpfBoostedBidPriceFetch(payload, { isDisabled });

  return (
    <WhenLoaded loading={isLoading}>
      {data && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          <Typography>
            {t(
              'You have added curated fees to the deal. Effectively dsp has to bid at least {amount} in order to cover these fees and deal floor/price.',
              {
                amount: priceFormat(data.bidPriceCpm, observableCurrency ?? DEFAULT_CURRENCY),
              }
            )}
          </Typography>

          <Typography
            sx={{
              mt: 1.5,
            }}
          >
            {t('Remember to communicate with buyer to set up correct deal price in DSP.')}
          </Typography>
        </Alert>
      )}
    </WhenLoaded>
  );
};
